import { createRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { palettes, tokens } from '../theme/tokens';

export type ModeSwitchValue = 'personal' | 'managing';

const Container = styled(Flex)`
	position: relative;
	align-items: center;
	box-sizing: border-box;
	min-width: 0;
	max-width: 100%;
	height: 40px;
	padding: 4px;
	border-radius: ${tokens.radius.full}px;
	background: ${palettes.softBeige['500']};
	box-shadow: 0 0 0 1px ${tokens.border.subtle};

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		box-shadow: ${tokens.shadow.inset};
		pointer-events: none;
		z-index: 2;
	}
`;

const Segment = styled.div<{ $selected?: boolean; $managing?: boolean }>`
	flex: 1 1 0;
	min-width: 0;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	z-index: 1;
	padding: 4px ${tokens.padding.compact}px;
	box-sizing: border-box;
	text-align: center;
	cursor: pointer;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: ${tokens.typography.sizes.small}px;
	line-height: ${tokens.typography.lineHeights.small};
	letter-spacing: 0.13px;
	font-family: ${tokens.typography.fontFamily};
	font-weight: ${tokens.typography.weights.medium};
	border-radius: ${tokens.radius.full}px;
	color: ${(props) =>
		props.$selected && props.$managing ? tokens.text.white : tokens.text.secondary};

	&:hover {
		color: ${(props) =>
			props.$selected && props.$managing ? tokens.text.white : tokens.text.primary};
	}

	${(props) =>
		props.$selected &&
		!props.$managing &&
		`
		color: ${tokens.text.primary};
	`}
`;

const Selected = styled(Box)<{ $managing?: boolean }>`
	position: absolute;
	top: 4px;
	height: 32px;
	z-index: 0;
	box-sizing: border-box;
	background: ${(props) => (props.$managing ? tokens.surface.sidebar : tokens.surface.white)};
	border: 1px solid ${(props) => (props.$managing ? tokens.border.focus : tokens.border.subtle)};
	box-shadow: ${tokens.shadow.button};
	border-radius: ${tokens.radius.full}px;
	pointer-events: none;
	transition:
		left 300ms cubic-bezier(0, 1, 0, 1),
		width 300ms cubic-bezier(0, 1, 0, 1),
		background 300ms cubic-bezier(0, 1, 0, 1),
		border-color 300ms cubic-bezier(0, 1, 0, 1);
`;

export function ModeSwitch(props: {
	value: ModeSwitchValue;
	onChange: (value: ModeSwitchValue) => void;
	personalLabel: string;
	managingLabel: string;
	width?: string | number;
}) {
	const options = useMemo(
		() =>
			[
				{ title: props.personalLabel, value: 'personal' as const },
				{ title: props.managingLabel, value: 'managing' as const },
			] as const,
		[props.personalLabel, props.managingLabel],
	);
	const segmentRefs = useMemo(
		() => Array.from({ length: options.length }, () => createRef<HTMLDivElement>()),
		[options.length],
	);
	const parentRef = useRef<HTMLDivElement | null>(null);
	const [selectedBounds, setSelectedBounds] = useState({ left: 0, width: 100 });
	const managing = props.value === 'managing';

	useLayoutEffect(() => {
		const measure = () => {
			const selectedIndex = options.findIndex(({ value }) => value === props.value);
			const selected = segmentRefs[selectedIndex]?.current;
			if (!selected) {
				return;
			}

			setSelectedBounds({
				left: selected.offsetLeft,
				width: selected.offsetWidth,
			});
		};

		measure();

		const parent = parentRef.current;
		if (!parent) {
			return;
		}

		const observer = new ResizeObserver(measure);
		observer.observe(parent);
		for (const ref of segmentRefs) {
			if (ref.current) {
				observer.observe(ref.current);
			}
		}

		return () => observer.disconnect();
	}, [segmentRefs, options, props.value]);

	return (
		<Container ref={parentRef} width={props.width ?? '100%'} role="radiogroup">
			<Selected style={selectedBounds} $managing={managing} />
			{options.map((option, index) => (
				<Segment
					key={option.value}
					role="radio"
					aria-checked={option.value === props.value}
					onClick={(element) => {
						setSelectedBounds({
							left: element.currentTarget.offsetLeft,
							width: element.currentTarget.offsetWidth,
						});

						props.onChange(option.value);
					}}
					$selected={option.value === props.value}
					$managing={managing}
					ref={segmentRefs[index]}
				>
					{option.title}
				</Segment>
			))}
		</Container>
	);
}
